import { YEARS_PER_CENTURY } from './date-picker.constants';
import {
  ALL_SIDES,
  Caret,
  CompiledPattern,
  FieldKind,
  FieldPart,
  FieldValues,
  PatternPartKind,
  SideSelector,
} from './date-picker.model';

/**
 * Slots are the single source of truth for the input: one raw text slot per field,
 * each exactly `field.width` characters, spaces where the user has not typed yet.
 *
 * A numeric model cannot express "the year is `2   ` while I am still typing", which
 * is why the editable state is raw text and the parsed values are derived from it.
 */

export const blankSlot = (width: number): string => ' '.repeat(width);

export function emptySlots(pattern: CompiledPattern): readonly string[] {
  return pattern.fields.map((field) => blankSlot(field.width));
}

export const isSlotBlank = (slot: string): boolean => slot.trim().length === 0;

/**
 * Every character of the field has been typed. Distinct from "not blank": a year slot
 * holding `2   ` is not blank, but it is not a year either.
 */
export const isSlotFilled = (slot: string, width: number): boolean =>
  slot.length === width && !slot.includes(' ');

export const slotsAreEmpty = (slots: readonly string[]): boolean => slots.every(isSlotBlank);

/** Pads a slot back to its field width, so slot length is an invariant. */
export const normalizeSlot = (value: string, width: number): string =>
  value.length === width ? value : value.padEnd(width, ' ').slice(0, width);

export function renderDisplay(pattern: CompiledPattern, slots: readonly string[]): string {
  let result = '';

  for (const part of pattern.parts) {
    if (part.kind === PatternPartKind.Literal) {
      result += part.text;
    } else {
      result += normalizeSlot(slots[part.index] ?? '', part.width);
    }
  }

  return result;
}

/** Field occupying `position`, or null when the position sits on a literal. */
export function fieldAtPosition(pattern: CompiledPattern, position: number): FieldPart | null {
  if (position < 0 || position >= pattern.length) return null;
  const index = pattern.fieldIndexAt[position];
  return index == null ? null : (pattern.fields[index] ?? null);
}

/**
 * Field that input at `position` should land in: the one under the caret, or the
 * next one when the caret sits on a separator.
 */
export function fieldForInput(
  pattern: CompiledPattern,
  position: number,
): { field: FieldPart; offset: number } | null {
  const direct = fieldAtPosition(pattern, position);
  if (direct) return { field: direct, offset: position - direct.start };

  const next = pattern.fields.find((field) => field.start >= position);
  if (next) return { field: next, offset: 0 };

  // Past the last field — focusing an input puts the caret at the end, so falling
  // through to null here would make typing a no-op.
  const last = pattern.fields[pattern.fields.length - 1];
  if (!last) return null;

  return { field: last, offset: Math.min(Math.max(position - last.start, 0), last.width) };
}

/** The field a freshly focused control should activate. */
export function firstEditableField(
  pattern: CompiledPattern,
  slots: readonly string[],
): FieldPart | null {
  return (
    pattern.fields.find((field) => isSlotBlank(slots[field.index] ?? '')) ??
    pattern.fields[0] ??
    null
  );
}

export function nextField(pattern: CompiledPattern, position: number): FieldPart | null {
  return pattern.fields.find((field) => field.start > position) ?? null;
}

export function prevField(pattern: CompiledPattern, position: number): FieldPart | null {
  for (let i = pattern.fields.length - 1; i >= 0; i--) {
    const field = pattern.fields[i]!;
    if (field.start < position) return field;
  }
  return null;
}

/** Caret that selects a whole field, which is how a segment reads as "active". */
export const selectField = (field: FieldPart): Caret => [field.start, field.end];

export const caretAt = (position: number): Caret => [position, position];

/** Every field the given selection touches, even partially. */
export function fieldsInRange(
  pattern: CompiledPattern,
  start: number,
  end: number,
): readonly FieldPart[] {
  if (start === end) {
    const field = fieldAtPosition(pattern, start);
    return field ? [field] : [];
  }
  return pattern.fields.filter((field) => field.start < end && field.end > start);
}

function formatFieldValue(field: FieldPart, values: FieldValues): string | null {
  switch (field.field) {
    case FieldKind.Year: {
      if (values.year === null) return null;
      const year = field.width === 2 ? values.year % YEARS_PER_CENTURY : values.year;
      return String(year).padStart(field.width, '0');
    }
    case FieldKind.Month:
      return values.month === null ? null : String(values.month).padStart(2, '0');
    case FieldKind.Day:
      return values.day === null ? null : String(values.day).padStart(2, '0');
    case FieldKind.Hour24:
      return values.hour24 === null ? null : String(values.hour24).padStart(2, '0');
    case FieldKind.Hour12:
      return values.hour12 === null ? null : String(values.hour12).padStart(2, '0');
    case FieldKind.Minute:
      return values.minute === null ? null : String(values.minute).padStart(2, '0');
    case FieldKind.DayPeriod:
      return values.dayPeriod;
  }
}

/**
 * Writes values into the slots of one range side, leaving the other side untouched.
 * `side` defaults to every field, which is what non-range patterns need.
 */
export function slotsFromValues(
  pattern: CompiledPattern,
  slots: readonly string[],
  values: FieldValues,
  side: SideSelector = ALL_SIDES,
  blankMissing = true,
): readonly string[] {
  const next = [...slots];

  for (const field of pattern.fields) {
    if (side !== ALL_SIDES && field.side !== side) continue;
    const formatted = formatFieldValue(field, values);
    if (formatted === null) {
      if (blankMissing) next[field.index] = blankSlot(field.width);
      continue;
    }
    next[field.index] = formatted;
  }

  return next;
}

/**
 * Reads slots back out of a rendered string, used to recover from edits the mask did
 * not intercept (undo, IME, autofill). Returns null when the string is not our shape.
 */
export function slotsFromDisplay(
  pattern: CompiledPattern,
  display: string,
): readonly string[] | null {
  if (display.length !== pattern.length) return null;

  const slots: string[] = new Array(pattern.fields.length).fill('');

  for (const part of pattern.parts) {
    const chunk = display.slice(part.start, part.end);
    if (part.kind === PatternPartKind.Literal) {
      if (chunk !== part.text) return null;
      continue;
    }
    slots[part.index] = chunk;
  }

  return slots;
}

export function writeSlot(
  slots: readonly string[],
  index: number,
  value: string,
): readonly string[] {
  const next = [...slots];
  next[index] = value;
  return next;
}

/** Clears the slots of one range side. */
export function clearSlots(
  pattern: CompiledPattern,
  slots: readonly string[],
  side: SideSelector = ALL_SIDES,
): readonly string[] {
  const next = [...slots];

  for (const field of pattern.fields) {
    if (side !== ALL_SIDES && field.side !== side) continue;
    next[field.index] = blankSlot(field.width);
  }

  return next;
}
